import React from 'react';
import Image from 'next/image';

import type {ErrorBoundaryCaptureInfo} from './types';
import {BoundarySource} from './types';
import {
  createCaptureInfoFromParsedError,
  createComponentCaptureInfo,
  createErrorInstance,
  parseBoundaryError,
  serializeClientBoundaryError,
} from './utils';

import styles from './styles.module.scss';

export type {ErrorBoundaryCaptureInfo} from './types';
export {BoundarySource} from './types';

export interface ErrorBoundaryProps {
  children?: React.ReactNode;
  environment: string;
  captureException: (error: Error, errorInfo: ErrorBoundaryCaptureInfo) => void;
  error?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: string | null;
  source: BoundarySource;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    // Define a state variable to track whether is an error or not
    const parsedError = props.error
      ? parseBoundaryError(props.error)
      : undefined;

    this.state = {
      hasError: !!props.error,
      error: props.error ?? null,
      source: parsedError?.source ?? BoundarySource.CLIENT,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: serializeClientBoundaryError(error),
      source: BoundarySource.CLIENT,
    };
  }

  componentDidMount(): void {
    // Fire `captureException` if error is coming from props
    if (this.state.hasError && this.state.error) {
      const parsedError = parseBoundaryError(this.state.error);

      this.props.captureException(
        createErrorInstance(parsedError),
        createCaptureInfoFromParsedError(parsedError),
      );
    }
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // You can use your own error logging service here
    this.props.captureException(error, createComponentCaptureInfo(errorInfo));

    // Trigger error state and UI
    this.setState({
      hasError: true,
      error: serializeClientBoundaryError(error),
      source: BoundarySource.CLIENT,
    });
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorBoundary__message}>
            <Image
              src="/static/assets/zoe_logo_circle.svg"
              alt="Zoe Financial"
              width={80}
              height={80}
            />
            <h2>Oops, there was an error!</h2>
            <p>Our Product Team was notified and will fix it soon.</p>
            <p>
              Please contact our support team at{' '}
              <a href="tel:6466062443">(646) 606-2443</a> or via email{' '}
              <a href="mailto:clientsupport@zoefin.com">
                clientsupport@zoefin.com{' '}
              </a>{' '}
              {`to know more details. this error was captured on ${this.state.source} side.`}
            </p>
            {this.props.environment !== 'production' && (
              <div className={styles.errorBoundary__message_error}>
                <h3>Error detail:</h3>
                <pre>{this.state.error}</pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export default ErrorBoundary;
